import React from 'react';
import {
  InternalApiAlertDecisionRule,
  InternalApiAlertDecisionRuleCommand,
  InternalApiScreeningAlertStatus,
} from '../../api-internal';
import { SalvButton, SalvCheckbox, SalvDropdown, SalvNumberField, SalvTextField, Size } from '@salvapp/ui-components';
import { formatEnumLabel } from '../../common/helper';

interface Props {
  rule: InternalApiAlertDecisionRule;
  statuses: InternalApiScreeningAlertStatus[];
}

interface EditingProps extends Props {
  ruleIndex: number;
  updateRule: (rule: InternalApiAlertDecisionRule | null, index: number) => void;
}

const generateCommandDescription = (rule: InternalApiAlertDecisionRule) => {
  switch (rule.runCommand) {
    case InternalApiAlertDecisionRuleCommand.ALL_ARE:
      return 'All';
    case InternalApiAlertDecisionRuleCommand.AT_LEAST_ONE_IS:
      return 'At least one';
    case InternalApiAlertDecisionRuleCommand.NONE_ARE:
      return 'None';
  }
};

const generateCommandValues = (rule: InternalApiAlertDecisionRule) => {
  return rule.values.map((value, index) => (
    <span key={value}>
      <b>{value}</b>
      {index != rule.values.length - 1 && ' or '}
    </span>
  ));
};

const DecisionRuleRow = ({ rule }: Props) => (
  <tr>
    <td>{rule.sortOrder}</td>
    <td>{rule.runOnlyOnFinal ? 'Yes' : 'No'}</td>
    <td>
      <span>
        <b>{generateCommandDescription(rule)}</b>
        {' of the alert statuses should be '}
        {generateCommandValues(rule)}
      </span>
    </td>
    <td>{rule.name}</td>
  </tr>
);

const statusesToOptions = (statuses: InternalApiScreeningAlertStatus[]) => {
  return statuses.map((status) => ({
    key: status.id!.toString(),
    label: status.textName + (status.endTime ? ` (deleted at ${status.endTime})` : ''),
    value: status.code,
  }));
};

const commandToValue = (command: InternalApiAlertDecisionRuleCommand) => {
  return {
    key: command,
    label: formatEnumLabel(command),
    value: command,
  };
};

const DecisionRuleEditingRow = ({ rule, statuses, ruleIndex, updateRule }: EditingProps) => {
  const activeStatuses = statuses.filter((status) => !status.endTime);
  const uniqueOldStatuses = statuses.filter(
    (status) => !activeStatuses.find((activeStatus) => activeStatus.code === status.code)
  );
  const statusesOptions = statusesToOptions(activeStatuses.concat(uniqueOldStatuses));
  const selectedStatuses = statusesOptions.filter((status) => rule.values.includes(status.value));
  return (
    <tr>
      <td>
        <SalvNumberField
          ariaLabel="Rule's priority"
          size={Size.SMALL}
          value={rule.sortOrder}
          onChange={(value) => {
            updateRule({ ...rule, sortOrder: value }, ruleIndex);
          }}
        />
      </td>
      <td>
        <SalvCheckbox
          ariaLabel='All alerts should be in final state'
          selected={rule.runOnlyOnFinal}
          onChange={(value) => {
            updateRule({ ...rule, runOnlyOnFinal: value }, ruleIndex);
          }}
        />
      </td>
      <td>
        <SalvDropdown
          ariaLabel='Matching type'
          options={[
            commandToValue(InternalApiAlertDecisionRuleCommand.ALL_ARE),
            commandToValue(InternalApiAlertDecisionRuleCommand.AT_LEAST_ONE_IS),
            commandToValue(InternalApiAlertDecisionRuleCommand.NONE_ARE),
          ]}
          value={commandToValue(rule.runCommand)}
          size={Size.SMALL}
          onChange={(value) => {
            updateRule({ ...rule, runCommand: value[0].value as InternalApiAlertDecisionRuleCommand }, ruleIndex);
          }}
        />
      </td>
      <td>
        <SalvDropdown
          ariaLabel='Matching against'
          options={statusesOptions}
          value={selectedStatuses}
          isMulti={true}
          size={Size.SMALL}
          onChange={(value) => {
            updateRule({ ...rule, values: [...new Set(value.map((option) => option.value))] }, ruleIndex);
          }}
        />
      </td>
      <td>
        <SalvTextField
          ariaLabel='Decision name'
          value={rule.name}
          size={Size.SMALL}
          onChange={(value) => {
            updateRule({ ...rule, name: value }, ruleIndex);
          }}
        />
      </td>
      <td>
        <SalvButton
          onClick={() => {
            updateRule(null, ruleIndex);
          }}
          size={Size.SMALL}
          label={'Remove'}
          variant={'danger'}
        />
      </td>
    </tr>
  );
};

const DecisionRuleHeader = () => {
  return (
    <tr>
      <th>Rule&#39;s priority</th>
      <th>All alerts should be in final state</th>
      <th>Alert statuses matching condition</th>
      <th>Decision</th>
    </tr>
  );
};
const DecisionRuleEditingHeader = () => {
  return (
    <tr>
      <th>Rule&#39;s priority</th>
      <th>All alerts should be final</th>
      <th>Matching type</th>
      <th>Matching against</th>
      <th>Decision</th>
      <th>Actions</th>
    </tr>
  );
};

export { DecisionRuleRow, DecisionRuleEditingRow, DecisionRuleHeader, DecisionRuleEditingHeader };
